import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { getScorecard, ScorecardData } from '../../redux/actions/scorecardAction'
import { CLAIM_OFFER, STORIES } from 'routes/routeConstant'
import './scorecard.css'

const OFFER_LINK = `${CLAIM_OFFER}?utm_source=series_scorecard&utm_medium=email&utm_campaign=sunday-series&utm_content=share-page`
// The brand name, the same in every language.
const BRAND = 'maayot'
/** Cards are 224 wide with a 14px gap. */
const CARD_STEP = 224 + 14

async function cardFile(src: string, n: number): Promise<File> {
  const blob = await (await fetch(src)).blob()
  return new File([blob], `maayot-scorecard-${n}.png`, { type: 'image/png' })
}

function download(file: File) {
  const url = URL.createObjectURL(file)
  const a = document.createElement('a')
  a.href = url
  a.download = file.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * /scorecard?t=<signed token> — the page behind "See and share my maayot Scorecard" in the email. The
 * learner swipes their five cards, then shares one (the phone's share sheet: Instagram, WeChat,
 * WhatsApp) or saves it. Needs a login (the email's link signs them in); the token must be their own.
 */
const Scorecard: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const [params] = useSearchParams()
  const token = params.get('t') || ''
  const [data, setData] = useState<ScorecardData | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'invalid'>('loading')
  const [active, setActive] = useState(0)
  const deck = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let live = true
    if (!token) {
      setState('invalid')
      return
    }
    getScorecard(token).then((res) => {
      if (!live) return
      setData(res)
      setState(res ? 'ready' : 'invalid')
    })
    return () => {
      live = false
    }
  }, [token])

  const onScroll = () => {
    const el = deck.current
    if (!el || !data) return
    setActive(Math.min(data.cards.length - 1, Math.max(0, Math.round(el.scrollLeft / CARD_STEP))))
  }

  const save = async (n: number) => {
    if (!data) return
    try {
      download(await cardFile(data.cards[n], n + 1))
    } catch {
      window.open(data.cards[n], '_blank')
    }
  }

  const share = async () => {
    if (!data) return
    try {
      const file = await cardFile(data.cards[active], active + 1)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: i18n.t('dashboard:scorecard.shareTitle') })
      } else {
        download(file)
      }
    } catch (error) {
      // Closing the share sheet is not an error; anything else (a failed fetch) opens the card instead.
      if ((error as Error)?.name !== 'AbortError') window.open(data.cards[active], '_blank')
    }
  }

  return (
    <main className='sc-page'>
      <div className='sc-top'>
        <span>{BRAND}</span>
      </div>

      {state === 'loading' && <p className='sc-note'>{t('scorecard.loading')}</p>}

      {state === 'invalid' && (
        <section className='sc-hero'>
          <h1>{t('scorecard.invalidTitle')}</h1>
          <p>{t('scorecard.invalidBody')}</p>
          <a className='sc-cta' href={STORIES}>
            {t('scorecard.goToStories')}
          </a>
        </section>
      )}

      {state === 'ready' && data && (
        <>
          <section className='sc-hero'>
            <h1>{t('scorecard.title')}</h1>
            <p>{t('scorecard.swipe')}</p>
          </section>
          <div className='sc-deck' ref={deck} onScroll={onScroll}>
            {data.cards.map((src, i) => (
              <div className='sc-card' key={src} style={{ ['--i' as any]: i }}>
                <img src={src} alt={t('scorecard.cardAlt', { number: String(i + 1), total: String(data.cards.length) })} />
                <button type='button' className='sc-link' onClick={() => save(i)}>
                  {t('scorecard.save')}
                </button>
              </div>
            ))}
          </div>
          <div className='sc-dots' aria-hidden='true'>
            {data.cards.map((src, i) => (
              <i key={src} className={i === active ? 'on' : ''} />
            ))}
          </div>
          <section className='sc-actions'>
            <button type='button' className='sc-cta' onClick={share}>
              {t('scorecard.share')}
            </button>
            <p className='sc-hint'>{t('scorecard.hint')}</p>
          </section>
          {data.offer && (
            <section className='sc-offer'>
              <div className='sc-offer-title'>{t('scorecard.offer.title')}</div>
              <div className='sc-offer-body'>{t('scorecard.offer.body')}</div>
              <div className='sc-offer-valid'>{t('scorecard.offer.valid', { date: data.offer.endsOn })}</div>
              <a className='sc-cta' href={OFFER_LINK}>
                {t('scorecard.offer.claim')}
              </a>
              <div className='sc-offer-fine'>{t('scorecard.offer.fine')}</div>
            </section>
          )}
        </>
      )}

      <footer className='sc-foot'>
        <b>{BRAND}</b>
        <br />
        {t('scorecard.madeWith')}
      </footer>
    </main>
  )
}

export default Scorecard
